import * as React from 'react'
import { authService } from '@/services/AuthService'
import { tokenStorage } from '@/helpers/tokenStorage'
import { isApiError } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import type {
  LoginInput,
  LoginResponse,
  User,
} from '@/schemas/auth.schema'

interface AuthContextValue {
  /** The signed-in user, or `null` when signed out. */
  user: User | null
  /** The current access token, or `null` when signed out. */
  token: string | null
  /** True while the initial "restore session from token" check is in flight. */
  isLoading: boolean
  /** Whether a user is currently authenticated. */
  isAuthenticated: boolean
  /** Exchange credentials for a session; resolves once the user is loaded. */
  login: (input: LoginInput) => Promise<void>
  /**
   * Adopt a session the app was handed rather than one it fetched — currently
   * only from accepting an invite, which returns a token for the account it
   * just created. Signing the new user straight in beats bouncing them to a
   * login form for a password they set ten seconds ago.
   */
  adoptSession: (session: LoginResponse) => void
  /** Clear the session and stored token. */
  logout: () => void
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * Whether the server looked at the session and turned it down: an expired or
 * revoked token, or an account that no longer exists. Only that ends a
 * session. Failing to reach the server says nothing about the token.
 */
function isRefusal<E>(error: E): boolean {
  return isApiError(error) && (error.status === 401 || error.status === 403)
}

/**
 * Provides authentication state to the app. On mount it restores a session from
 * a persisted token (by fetching the current user). A restore the server
 * refuses clears the token and drops to the signed-out state; one that cannot
 * reach the server keeps the session and the user it last confirmed, because
 * the survey screens are used where there is no signal, and their unsynced
 * work is only reachable signed in.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // helpers → state → effects → handlers → render
  const [user, setUser] = React.useState<User | null>(null)
  const [token, setToken] = React.useState<string | null>(() => tokenStorage.get())
  const [isLoading, setIsLoading] = React.useState<boolean>(true)

  React.useEffect(() => {
    let active = true

    async function restoreSession(): Promise<void> {
      if (!tokenStorage.get()) {
        setIsLoading(false)
        return
      }
      try {
        const currentUser = await authService.me()
        tokenStorage.setUser(currentUser)
        if (active) setUser(currentUser)
      } catch (error) {
        if (isRefusal(error)) {
          logger.warn('Session restore refused; signing out', {
            error: toErrorMessage(error),
          })
          tokenStorage.clear()
          if (active) {
            setUser(null)
            setToken(null)
          }
          return
        }
        // No signal, or the API is down. The token is kept either way, so the
        // next load with a connection can check it; without a remembered user
        // there is nobody to show as signed in until then.
        logger.warn('Could not reach the server to restore the session', {
          error: toErrorMessage(error),
        })
        if (active) setUser(tokenStorage.getUser())
      } finally {
        if (active) setIsLoading(false)
      }
    }

    void restoreSession()
    return () => {
      active = false
    }
  }, [])

  const login = React.useCallback(async (input: LoginInput): Promise<void> => {
    const result = await authService.login(input)
    tokenStorage.set(result.accessToken)
    tokenStorage.setUser(result.user)
    setToken(result.accessToken)
    setUser(result.user)
  }, [])

  const adoptSession = React.useCallback((session: LoginResponse): void => {
    tokenStorage.set(session.accessToken)
    tokenStorage.setUser(session.user)
    setToken(session.accessToken)
    setUser(session.user)
  }, [])

  const logout = React.useCallback((): void => {
    tokenStorage.clear()
    setToken(null)
    setUser(null)
  }, [])

  const value: AuthContextValue = {
    user,
    token,
    isLoading,
    isAuthenticated: user !== null,
    login,
    adoptSession,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Access the auth context. Throws if used outside an {@link AuthProvider} so a
 * misplaced consumer fails loudly instead of reading a stale default.
 */
export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
